import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { theme } from '../../styles/theme';
import { auth } from '../../../auth/appAuth';
import { sendCustomVerificationEmail } from '../../../auth/api/emailVerificationApi';

const BannerContainer = styled.div`
  background: #e8f2ff;
  border-bottom: 1px solid ${theme.colors.primary.main}25;
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  position: fixed;
  left: 0;
  right: 0;
  ${({ variant }) => (variant === 'app'
    ? `
      top: 0;
      z-index: 1001;
    `
    : `
      top: 72px;
      z-index: 99;
    `)}
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  position: relative;

  ${({ variant }) => variant === 'app' && `
    max-width: none;
    justify-content: center;
    padding-right: 220px;
    padding-left: 220px;
    text-align: center;

    @media (max-width: 768px) {
      flex-direction: column;
      padding-right: ${theme.spacing['2xl']};
      padding-left: ${theme.spacing['2xl']};
    }
  `}

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing.md};
    text-align: center;
  }
`;

const Message = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  ${({ variant }) => variant === 'app' && `
    justify-content: center;
  `}

  svg {
    width: 24px;
    height: 24px;
    color: ${theme.colors.primary.main};
    flex-shrink: 0;
  }
`;

const ActionButton = styled.button`
  background: ${theme.colors.primary.main};
  color: white;
  border: none;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;

  ${({ variant }) => variant === 'app' && `
    position: absolute;
    right: ${theme.spacing['2xl']};
    top: 50%;
    transform: translateY(-50%);

    @media (max-width: 768px) {
      position: static;
      transform: none;
    }
  `}

  &:hover:not(:disabled) {
    background: ${theme.colors.primary.dark};
    transform: ${({ variant }) => (
      variant === 'app' ? 'translateY(calc(-50% - 1px))' : 'translateY(-1px)'
    )};

    @media (max-width: 768px) {
      transform: translateY(-1px);
    }
  }

  &:disabled {
    background: ${theme.colors.primary.main}40;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const EmailVerificationBanner = ({ variant = 'website' }) => {
  const [sending, setSending] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [feedback, setFeedback] = useState(null); // 'success' | 'error'

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const t = setInterval(() => setResendCooldownSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldownSeconds]);

  const handleResendVerification = async () => {
    if (sending || resendCooldownSeconds > 0) return;
    try {
      setSending(true);
      setFeedback(null);
      const user = auth.currentUser;
      if (user) {
        const idToken = await user.getIdToken();
        await sendCustomVerificationEmail(idToken);
        setResendCooldownSeconds(120);
        setFeedback('success');
      }
    } catch (error) {
      console.error('Error sending verification email:', error);
      if (typeof error?.retryAfterSeconds === 'number' && error.retryAfterSeconds > 0) {
        setResendCooldownSeconds(error.retryAfterSeconds);
      }
      setFeedback('error');
    } finally {
      setSending(false);
    }
  };

  const getButtonText = () => {
    if (sending) return 'Sending...';
    if (resendCooldownSeconds > 0) return `Wait ${resendCooldownSeconds}s`;
    return 'Resend Verification Email';
  };

  return (
    <BannerContainer id="email-verification-banner" variant={variant}>
      <BannerContent variant={variant}>
        <Message variant={variant}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span>
            Please verify your email address to access all features. Check your inbox (and spam folder) for the verification link.
            {feedback === 'success' && ' Verification email sent!'}
            {feedback === 'error' && ' Failed to send. Try again in a minute.'}
          </span>
        </Message>
        <ActionButton
          variant={variant}
          onClick={handleResendVerification}
          disabled={sending || resendCooldownSeconds > 0}
        >
          {getButtonText()}
        </ActionButton>
      </BannerContent>
    </BannerContainer>
  );
};

export default EmailVerificationBanner; 